"use client";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Message } from "@/lib/core/crm/types";
import { getProviderDisplayName, getProviderBadgeColor } from "@/lib/config/providers";

interface MessageHeaderProps {
  message: Message;
  className?: string;
}

export function MessageHeader({ message, className }: MessageHeaderProps) {
  // Defensive checks for sender data
  const senderName = message?.sender?.name || "Unknown Sender";
  const senderEmail = message?.sender?.email || "";

  const getSenderInitials = (name: string) => {
    if (!name || typeof name !== "string") return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getProviderBadge = () => {
    const displayName = getProviderDisplayName(String(message.provider));
    const badgeColor = getProviderBadgeColor(String(message.provider));

    return (
      <Badge variant="secondary" className={cn("text-xs", badgeColor)}>
        {displayName}
      </Badge>
    );
  };

  return (
    <div className={cn("flex items-center space-x-3 min-w-0 flex-1", className)}>
      <Avatar className="h-10 w-10 flex-shrink-0">
        <AvatarFallback className="text-sm">
          {getSenderInitials(senderName)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <h2 className="text-lg font-semibold line-clamp-1">{message.subject || "(No Subject)"}</h2>
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <span className="truncate" title={senderEmail}>
            {senderName}
          </span>
          {getProviderBadge()}
        </div>
      </div>
    </div>
  );
}
